import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Search, Save, X, UserCircle2, ChevronRight } from 'lucide-react';
import { MOCK_USERS, LEAD_SOURCES, User } from '../data/mockData';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
export function RateTables() {
  const [users, setUsers] = useState(MOCK_USERS.filter(u => u.model === 'BPS'));
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(users[0]?.id || null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, number>>({});
  const filteredUsers = useMemo(() => {
    return users.filter(u => u.name.toLowerCase().includes(search.toLowerCase()) || u.title.toLowerCase().includes(search.toLowerCase()));
  }, [users, search]);
  const selectedUser = users.find(u => u.id === selectedUserId);
  const handleSelectUser = (user: User) => {
    if (isEditing) {
      if (!window.confirm('You have unsaved changes. Discard?')) return;
    }
    setSelectedUserId(user.id);
    setIsEditing(false);
  };
  const startEditing = () => {
    if (selectedUser && selectedUser.rates) {
      setEditForm({
        ...selectedUser.rates
      });
      setIsEditing(true);
    }
  };
  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({});
  };
  const saveRates = () => {
    if (!selectedUserId) return;
    setUsers(users.map(u => u.id === selectedUserId ? {
      ...u,
      rates: editForm
    } : u));
    setIsEditing(false);
  };
  const handleRateChange = (source: string, value: string) => {
    const numValue = parseInt(value, 10);
    setEditForm(prev => ({
      ...prev,
      [source]: isNaN(numValue) ? 0 : numValue
    }));
  };
  return <div className="flex h-[calc(100vh-8rem)] gap-6" data-id="element-230">
      {/* Left: Master List (Fixed Width) */}
      <Card className="w-80 lg:w-96 flex flex-col overflow-hidden shadow-sm border-slate-200/60 shrink-0" data-id="element-231">
        <CardHeader className="border-b border-slate-100 pb-4 shrink-0 bg-white" data-id="element-232">
          <CardTitle data-id="element-233">BPS Users</CardTitle>
          <CardDescription data-id="element-234">Select a user to manage rates.</CardDescription>
          <div className="relative mt-3" data-id="element-235">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" data-id="element-236" />
            <Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-slate-50 border-slate-200 focus:bg-white" data-id="element-237" />
          </div>
        </CardHeader>
        <div className="flex-1 overflow-y-auto p-2 bg-slate-50/30" data-id="element-238">
          <div className="space-y-1" data-id="element-239">
            {filteredUsers.map(user => {
            const isSelected = selectedUserId === user.id;
            return <button key={user.id} onClick={() => handleSelectUser(user)} className={cn('w-full flex items-center gap-3 rounded-lg p-3 text-left transition-all duration-200 group', isSelected ? 'bg-white shadow-sm ring-1 ring-slate-200' : 'hover:bg-white hover:shadow-sm')} data-id="element-240">
                  <img src={user.avatar} alt={user.name} className="h-10 w-10 rounded-full bg-slate-200 border border-slate-100" data-id="element-241" />
                  <div className="flex-1 overflow-hidden" data-id="element-242">
                    <div className={cn('font-semibold truncate text-sm', isSelected ? 'text-slate-900' : 'text-slate-700')} data-id="element-243">
                      {user.name}
                    </div>
                    <div className="text-xs text-slate-500 truncate font-medium" data-id="element-244">
                      {user.title}
                    </div>
                  </div>
                  <ChevronRight className={cn('h-4 w-4 transition-transform', isSelected ? 'text-slate-400 translate-x-0' : 'text-transparent -translate-x-2 group-hover:text-slate-300 group-hover:translate-x-0')} data-id="element-245" />
                </button>;
          })}
            {filteredUsers.length === 0 && <div className="p-8 text-center text-sm text-slate-500 flex flex-col items-center" data-id="element-246">
                <Search className="h-6 w-6 mb-2 text-slate-300" data-id="element-247" />
                No users found.
              </div>}
          </div>
        </div>
      </Card>

      {/* Right: Detail View (Fluid Width) */}
      <Card className="flex-1 flex flex-col overflow-hidden shadow-sm border-slate-200/60 bg-white" data-id="element-248">
        <AnimatePresence mode="wait" data-id="element-249">
          {selectedUser ? <motion.div key={selectedUser.id} initial={{
          opacity: 0,
          x: 10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -10
        }} transition={{
          duration: 0.15
        }} className="flex flex-col h-full" data-id="element-250">
              <CardHeader className="border-b border-slate-100 shrink-0 flex flex-row items-start justify-between bg-white" data-id="element-251">
                <div className="flex items-center gap-5" data-id="element-252">
                  <img src={selectedUser.avatar} alt={selectedUser.name} className="h-16 w-16 rounded-full bg-slate-200 shadow-sm border border-slate-100" data-id="element-253" />
                  <div data-id="element-254">
                    <CardTitle className="text-2xl" data-id="element-255">
                      {selectedUser.name}
                    </CardTitle>
                    <CardDescription className="text-sm font-medium mt-1" data-id="element-256">
                      {selectedUser.title}
                    </CardDescription>
                  </div>
                </div>
                <div data-id="element-257">
                  {!isEditing ? <Button onClick={startEditing} className="bg-slate-900 text-white hover:bg-slate-800" data-id="element-258">
                      Edit Rates
                    </Button> : <div className="flex gap-2" data-id="element-259">
                      <Button variant="outline" onClick={cancelEditing} data-id="element-260">
                        Cancel
                      </Button>
                      <Button onClick={saveRates} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm" data-id="element-261">
                        <Save className="mr-2 h-4 w-4" data-id="element-262" /> Save Changes
                      </Button>
                    </div>}
                </div>
              </CardHeader>
              <div className="flex-1 overflow-y-auto p-8 bg-slate-50/30" data-id="element-263">
                <div className="max-w-3xl mx-auto" data-id="element-264">
                  <div className="grid grid-cols-2 gap-y-2 border-b border-slate-200 pb-3 mb-4 text-sm font-bold text-slate-500 uppercase tracking-wider" data-id="element-265">
                    <div data-id="element-266">Lead Source</div>
                    <div className="text-right" data-id="element-267">Basis Points (BPS)</div>
                  </div>
                  <div className="space-y-2" data-id="element-268">
                    {LEAD_SOURCES.map(source => <div key={source} className="flex items-center justify-between group p-3 rounded-lg hover:bg-white hover:shadow-sm transition-all border border-transparent hover:border-slate-100" data-id="element-269">
                        <div className="text-sm font-semibold text-slate-700" data-id="element-270">
                          {source}
                        </div>
                        <div className="flex items-center gap-2" data-id="element-271">
                          {isEditing ? <div className="flex items-center" data-id="element-272">
                              <Input type="number" className="w-24 text-right font-semibold bg-white border-blue-200 focus:border-blue-500 focus:ring-blue-500" value={editForm[source] ?? 0} onChange={e => handleRateChange(source, e.target.value)} data-id="element-273" />
                              <span className="ml-3 text-sm font-medium text-slate-400 w-8" data-id="element-274">
                                bps
                              </span>
                            </div> : <div className="flex items-center" data-id="element-275">
                              <Badge variant="secondary" className="text-sm px-3 py-1 font-bold bg-slate-100 text-slate-700" data-id="element-276">
                                {selectedUser.rates?.[source] || 0}
                              </Badge>
                              <span className="ml-3 text-sm font-medium text-slate-400 w-8" data-id="element-277">
                                bps
                              </span>
                            </div>}
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </motion.div> : <div className="flex-1 flex flex-col items-center justify-center text-slate-500 bg-slate-50/50" data-id="element-278">
              <UserCircle2 className="h-16 w-16 mb-4 text-slate-300" data-id="element-279" />
              <p className="font-medium" data-id="element-280">
                Select a user to view their rate table
              </p>
            </div>}
        </AnimatePresence>
      </Card>
    </div>;
}